import { useQuery } from '@apollo/client';
import {
  AgencySearchProfileCardFragment,
  GetAgencyProfileRatingQuery,
} from '../../../generated/graphql';
import {
  getSearchResultRating,
  shouldFetchProfileRating,
} from '../../../utils/search-ratings';
import getDepthTierLabel, { DepthTierLabel } from '../../../utils/depth-tier';
import { GET_AGENCY_PROFILE_RATING } from '../../agency-profile-card/agency-profile-rating-query';

export interface AgencyRating {
  averageRating: number;
  totalReviews: number;
}

export interface AgencyRatingResult {
  rating?: AgencyRating;
  subCategory2: DepthTierLabel | 'None';
  reviewRating: boolean;
  reviewCount?: number;
  starRating?: number;
}

export const getAgencySubCategory2 = (
  profileTier?: string | null,
  depthTier?: string | null,
): AgencyRatingResult['subCategory2'] =>
  getDepthTierLabel(profileTier, depthTier) ?? 'None';

const useAgencyRating = (
  agency: AgencySearchProfileCardFragment,
): AgencyRatingResult => {
  const {
    data: profileData,
    loading: profileLoading,
    error: profileError,
  } = useQuery<GetAgencyProfileRatingQuery>(GET_AGENCY_PROFILE_RATING, {
    variables: { agencyId: agency.id },
    skip: !agency.id || !shouldFetchProfileRating(agency.profileTier),
  });

  const profile = profileData?.agency;
  const profileQueryHasNoData =
    profileLoading || Boolean(profileError) || !profile;
  const profileReviewCount = profile?.recommendations?.numberOfReviews;
  const profileStarRating = profile?.recommendations?.overallStarRating;
  const searchReviewCount = profileQueryHasNoData
    ? agency.reputation?.numberOfReviews
    : undefined;
  const searchStarRating = profileQueryHasNoData
    ? agency.reputation?.overallStarRating
    : undefined;

  const { rating, totalReviews } =
    getSearchResultRating({
      searchRecentReviewCount: agency.reputation?.numberOfReviewsRecent,
      profileRating: profileStarRating,
      profileReviewCount,
      profileLoading,
      profileTier: agency.profileTier,
      depthTier: profile?.depthTier,
    }) ?? {};

  return {
    rating:
      rating === undefined || totalReviews === undefined
        ? undefined
        : { averageRating: rating, totalReviews },
    // Temporary until agency search exposes depthTier synchronously.
    subCategory2: getAgencySubCategory2(agency.profileTier, profile?.depthTier),
    reviewRating:
      Boolean(rating) ||
      (typeof searchStarRating === 'number' &&
        typeof searchReviewCount === 'number' &&
        searchReviewCount > 0),
    reviewCount: profileReviewCount ?? searchReviewCount ?? undefined,
    starRating: profileStarRating ?? searchStarRating ?? undefined,
  };
};

export default useAgencyRating;
